/**
 * Have-Done Landing Page
 *
 * A warm, celebratory landing page that converts visitors to users.
 * Focuses on the emotional benefit: celebrate what you've accomplished.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  TrendingUp,
  Calendar,
  Trophy,
  CheckCircle2,
  ArrowRight,
  Star,
  Smartphone,
  Monitor,
  Heart,
  Zap,
  Shield,
  ChevronDown,
} from 'lucide-react';
import { useConfetti } from '../hooks/useConfetti';

interface LandingPageProps {
  onGetStarted: () => void;
  onLogin: () => void;
}

// Hook for scroll-based fade-in animations
function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

export function LandingPage({ onGetStarted, onLogin }: LandingPageProps) {
  const confettiTriggerRef = useRef<HTMLDivElement>(null);
  const hasTriggeredConfetti = useRef(false);
  const { celebrateBigWin } = useConfetti();

  // Trigger confetti when scrolling to the "How It Works" section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredConfetti.current) {
          hasTriggeredConfetti.current = true;
          celebrateBigWin();
        }
      },
      { threshold: 0.5 }
    );

    if (confettiTriggerRef.current) {
      observer.observe(confettiTriggerRef.current);
    }

    return () => observer.disconnect();
  }, [celebrateBigWin]);
  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-bg-primary/80 backdrop-blur-md border-b border-border-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-display text-xl text-text-primary">Have-Done</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="#features"
                className="hidden sm:block text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="hidden sm:block text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                How It Works
              </a>
              <button
                onClick={onLogin}
                className="text-sm font-medium text-text-primary hover:text-primary transition-colors"
              >
                Log in
              </button>
              <button
                onClick={onGetStarted}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark
                         rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-primary/20"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent-dark mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Celebrate your accomplishments</span>
            </div>

            {/* Headline */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-text-primary leading-tight mb-6 animate-slide-up">
              Start celebrating what you've{' '}
              <span className="relative">
                <span className="relative z-10 text-primary">done</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-accent/30 -z-0 rounded" />
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto mb-10 animate-slide-up" style={{ animationDelay: '100ms' }}>
              Have-Done flips productivity on its head. Instead of a stressful to-do list,
              build a growing record of your achievements. Feel motivated, not overwhelmed.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-slide-up" style={{ animationDelay: '200ms' }}>
              <button
                onClick={onGetStarted}
                className="group w-full sm:w-auto px-8 py-4 text-lg font-medium text-white bg-primary
                         hover:bg-primary-dark rounded-xl transition-all duration-300
                         shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-accent/30
                         ring-2 ring-transparent hover:ring-accent/50
                         flex items-center justify-center gap-2"
              >
                Start for Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#how-it-works"
                className="w-full sm:w-auto px-8 py-4 text-lg font-medium text-text-primary bg-bg-secondary
                         hover:bg-bg-tertiary rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                See How It Works
                <ChevronDown className="w-5 h-5" />
              </a>
            </div>

            {/* Platform indicators */}
            <div className="flex items-center justify-center gap-6 text-text-tertiary animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                <span className="text-sm">Web App</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-border" />
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm">iOS & Android</span>
              </div>
            </div>
          </div>

          {/* Hero Visual - App Preview */}
          <div className="mt-16 relative animate-scale-in" style={{ animationDelay: '400ms' }}>
            <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-transparent to-transparent z-10 pointer-events-none" />
            <div className="relative mx-auto max-w-4xl">
              {/* Decorative elements */}
              <div className="absolute -top-8 -left-8 w-24 h-24 bg-accent/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />

              {/* App Preview Card */}
              <div className="relative bg-bg-secondary rounded-2xl border border-border-light shadow-2xl shadow-text-primary/5 overflow-hidden">
                {/* Browser-like header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border-light bg-bg-primary">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-error/60" />
                    <div className="w-3 h-3 rounded-full bg-warning/60" />
                    <div className="w-3 h-3 rounded-full bg-success/60" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="px-4 py-1 rounded-md bg-bg-secondary text-xs text-text-tertiary">
                      have-done.app
                    </div>
                  </div>
                </div>

                {/* App Content Preview */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-display text-2xl text-text-primary">Today</h3>
                      <p className="text-sm text-text-secondary">January 18, 2026</p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10">
                      <span className="text-2xl">🔥</span>
                      <span className="text-sm font-medium text-accent-dark">12 day streak</span>
                    </div>
                  </div>

                  {/* Sample entries */}
                  <div className="space-y-3">
                    <EntryPreview
                      content="Completed the quarterly report ahead of deadline"
                      category="work"
                      categoryLabel="Work"
                      isBigWin
                    />
                    <EntryPreview
                      content="30 minute morning run in the park"
                      category="health"
                      categoryLabel="Health"
                    />
                    <EntryPreview
                      content="Finished a chapter in my leadership book"
                      category="learning"
                      categoryLabel="Learning"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-12 bg-bg-secondary border-y border-border-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16">
            <div className="text-center">
              <div className="font-display text-3xl text-primary mb-1">10,000+</div>
              <div className="text-sm text-text-secondary">Accomplishments logged</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border-light" />
            <div className="text-center">
              <div className="font-display text-3xl text-primary mb-1">4.9</div>
              <div className="flex items-center gap-1 justify-center mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <div className="text-sm text-text-secondary">App Store rating</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border-light" />
            <div className="text-center">
              <div className="font-display text-3xl text-primary mb-1">92%</div>
              <div className="text-sm text-text-secondary">Feel more productive</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl text-text-primary mb-4">
              Built for the way your mind works
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Traditional to-do apps remind you of failure. Have-Done celebrates every win,
              no matter how small, and helps you see your growth over time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <FeatureCard
              icon={<Zap className="w-6 h-6" />}
              title="Lightning-fast entry"
              description="Log what you've done in seconds. No friction, no organization paralysis. Just capture the win and move on."
              color="accent"
            />
            <FeatureCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Watch yourself grow"
              description="Your timeline fills up over time, creating a visual record of progress. See your accomplishments compound."
              color="primary"
            />
            <FeatureCard
              icon={<Calendar className="w-6 h-6" />}
              title="Weekly insights"
              description="Automatic summaries show patterns in your productivity. Discover your most creative days and peak hours."
              color="category-creative"
            />
            <FeatureCard
              icon={<Trophy className="w-6 h-6" />}
              title="Gentle gamification"
              description="Build streaks by logging daily. No guilt if you miss a day—just pick back up and keep celebrating."
              color="category-health"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8 bg-bg-secondary">
        <div className="max-w-6xl mx-auto">
          {/* Confetti trigger point */}
          <div ref={confettiTriggerRef} className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl text-text-primary mb-4">
              Simple as 1, 2, done
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              No complex setup or categorization systems. Just start logging and let the magic happen.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <StepCard
              number={1}
              title="Log your win"
              description="Finished a task? Learned something new? Had a good workout? Just write it down."
              visual={
                <div className="relative w-full h-32 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl" />
                  <div className="relative bg-white rounded-lg shadow-lg px-4 py-3 max-w-[200px]">
                    <div className="text-xs text-text-tertiary mb-1">What did you accomplish?</div>
                    <div className="text-sm text-text-primary">Shipped new feature ✨</div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              }
            />
            <StepCard
              number={2}
              title="Watch it grow"
              description="Your accomplishments build up over time, creating a timeline you can be proud of."
              visual={
                <div className="relative w-full h-32 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-category-work/5 to-category-health/5 rounded-xl" />
                  <div className="relative flex gap-2">
                    {['work', 'health', 'creative'].map((cat, i) => (
                      <div
                        key={cat}
                        className={`w-12 h-${16 + i * 4} rounded-lg ${
                          cat === 'work' ? 'bg-category-work h-16' :
                          cat === 'health' ? 'bg-category-health h-20' :
                          'bg-category-creative h-24'
                        }`}
                        style={{ height: `${64 + i * 16}px` }}
                      />
                    ))}
                  </div>
                </div>
              }
            />
            <StepCard
              number={3}
              title="Feel amazing"
              description="Review your week and realize just how much you've actually accomplished. Spoiler: it's a lot."
              visual={
                <div className="relative w-full h-32 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 rounded-xl" />
                  <div className="relative text-center">
                    <div className="text-4xl mb-2">🎉</div>
                    <div className="font-display text-lg text-primary">23 wins this week!</div>
                    <div className="text-xs text-text-secondary">+5 from last week</div>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </section>

      {/* The Difference Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl text-text-primary mb-4">
              The Have-Done difference
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Traditional to-do apps are designed around what you haven't done.
              We're designed around what you have.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Traditional To-Do */}
            <div className="relative p-6 rounded-2xl border-2 border-dashed border-border bg-bg-secondary/50">
              <div className="absolute -top-3 left-6 px-3 py-1 bg-bg-primary rounded-full text-sm font-medium text-text-tertiary border border-border-light">
                Traditional to-do apps
              </div>
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-3 text-text-tertiary">
                  <div className="w-5 h-5 rounded border-2 border-border" />
                  <span className="line-through">Finish quarterly report</span>
                </div>
                <div className="flex items-center gap-3 text-text-secondary">
                  <div className="w-5 h-5 rounded border-2 border-border" />
                  <span>Reply to 47 emails</span>
                </div>
                <div className="flex items-center gap-3 text-text-secondary">
                  <div className="w-5 h-5 rounded border-2 border-border" />
                  <span>Go to the gym</span>
                </div>
                <div className="flex items-center gap-3 text-text-secondary">
                  <div className="w-5 h-5 rounded border-2 border-border" />
                  <span>Learn that new framework</span>
                </div>
                <div className="flex items-center gap-3 text-error/80">
                  <div className="w-5 h-5 rounded border-2 border-error/50" />
                  <span>Overdue: Call mom</span>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-error/5 border border-error/20">
                <div className="text-sm text-error/80 font-medium">😰 4 tasks overdue</div>
                <div className="text-xs text-text-tertiary mt-1">You're falling behind...</div>
              </div>
            </div>

            {/* Have-Done */}
            <div className="relative p-6 rounded-2xl border-2 border-primary/30 bg-primary/5">
              <div className="absolute -top-3 left-6 px-3 py-1 bg-primary rounded-full text-sm font-medium text-white">
                Have-Done
              </div>
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-3 text-text-primary">
                  <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <span>Finished quarterly report early!</span>
                </div>
                <div className="flex items-center gap-3 text-text-primary">
                  <div className="w-5 h-5 rounded-full bg-category-work flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <span>Cleared 23 important emails</span>
                </div>
                <div className="flex items-center gap-3 text-text-primary">
                  <div className="w-5 h-5 rounded-full bg-category-health flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <span>20 min walk during lunch</span>
                </div>
                <div className="flex items-center gap-3 text-text-primary">
                  <div className="w-5 h-5 rounded-full bg-category-social flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <span>Great call with mom ❤️</span>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-success/10 border border-success/30">
                <div className="text-sm text-success font-medium">🎉 4 wins today!</div>
                <div className="text-xs text-text-secondary mt-1">You're on a roll. Keep it up!</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-bg-secondary">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl text-text-primary mb-4">
              Loved by people who get things done
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              From perfectionists to procrastinators, Have-Done helps everyone see their progress.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <TestimonialCard
              quote="I used to end every day feeling like I hadn't done enough. Now I actually see how much I accomplish. Game changer."
              author="Sarah K."
              role="Product Manager"
              avatar="👩‍💼"
            />
            <TestimonialCard
              quote="As someone with ADHD, to-do lists always felt overwhelming. Have-Done lets me celebrate wins without the guilt."
              author="Marcus T."
              role="Software Engineer"
              avatar="👨‍💻"
            />
            <TestimonialCard
              quote="I finally have proof for my imposter syndrome that I actually know what I'm doing. The weekly summaries are gold."
              author="Jamie L."
              role="Freelance Designer"
              avatar="🎨"
            />
            <TestimonialCard
              quote="I now log my coffee break as a win. And I feel great about it. What kind of magic is this?!"
              author="Emma V."
              role="Content Creator"
              avatar="☕"
              isPlayful
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <FeaturesGridSection />

      {/* Final CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark p-8 sm:p-12 lg:p-16 text-center">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Free to start</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Ready to celebrate<br />what you've done?
              </h2>

              <p className="text-lg text-white/80 max-w-xl mx-auto mb-10">
                Join 10,000+ people who've transformed their relationship with productivity.
                Your wins are waiting to be logged.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={onGetStarted}
                  className="group w-full sm:w-auto px-8 py-4 text-lg font-medium text-primary bg-white
                           hover:bg-bg-primary rounded-xl transition-all duration-300
                           shadow-lg hover:shadow-xl hover:shadow-accent/20
                           ring-2 ring-white/20 hover:ring-accent/50
                           flex items-center justify-center gap-2"
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <div className="flex items-center gap-4 text-white/70">
                  <div className="flex items-center gap-1">
                    <Monitor className="w-4 h-4" />
                    <span className="text-sm">Web</span>
                  </div>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <Smartphone className="w-4 h-4" />
                    <span className="text-sm">Mobile</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border-light">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6 text-sm text-text-secondary">
              <Link to="/privacy" className="hover:text-text-primary transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-text-primary transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-text-primary transition-colors">Contact</Link>
            </div>

            <p className="text-sm text-text-tertiary">
              Made with ❤️ in Amsterdam
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================================
   Sub-components
   ============================================ */

function EntryPreview({
  content,
  category,
  categoryLabel,
  isBigWin = false,
}: {
  content: string;
  category: string;
  categoryLabel: string;
  isBigWin?: boolean;
}) {
  const categoryColors: Record<string, string> = {
    work: 'bg-category-work/15 text-category-work border-category-work',
    health: 'bg-category-health/15 text-category-health border-category-health',
    creative: 'bg-category-creative/15 text-category-creative border-category-creative',
    learning: 'bg-category-learning/15 text-category-learning border-category-learning',
    personal: 'bg-category-personal/15 text-category-personal border-category-personal',
    social: 'bg-category-social/15 text-category-social border-category-social',
  };

  return (
    <div className={`p-4 rounded-xl bg-bg-primary border-l-4 ${categoryColors[category]?.split(' ')[2] || 'border-primary'} shadow-sm`}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm text-text-primary">{content}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[category]?.split(' ').slice(0, 2).join(' ')}`}>
              {categoryLabel}
            </span>
            {isBigWin && (
              <span className="inline-flex items-center gap-1 text-xs text-accent-dark">
                <Star className="w-3 h-3 fill-accent text-accent" />
                Big win
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    accent: 'bg-accent/10 text-accent-dark',
    'category-creative': 'bg-category-creative/15 text-category-creative',
    'category-health': 'bg-category-health/15 text-category-health',
  };

  return (
    <div className="group p-6 lg:p-8 rounded-2xl bg-bg-secondary border border-border-light hover:border-primary/30 hover:shadow-lg transition-all duration-300">
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${colorClasses[color]} mb-4`}>
        {icon}
      </div>
      <h3 className="font-display text-xl text-text-primary mb-2">{title}</h3>
      <p className="text-text-secondary leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({
  number,
  title,
  description,
  visual,
}: {
  number: number;
  title: string;
  description: string;
  visual: React.ReactNode;
}) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white font-display text-lg mb-6">
        {number}
      </div>
      {visual}
      <h3 className="font-display text-xl text-text-primary mt-6 mb-2">{title}</h3>
      <p className="text-text-secondary">{description}</p>
    </div>
  );
}

function TestimonialCard({
  quote,
  author,
  role,
  avatar,
  isPlayful = false,
}: {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  isPlayful?: boolean;
}) {
  return (
    <div className={`p-6 rounded-2xl bg-bg-primary border transition-all duration-300 hover:shadow-lg ${
      isPlayful ? 'border-accent/30 bg-gradient-to-br from-bg-primary to-accent/5' : 'border-border-light'
    }`}>
      <div className="flex items-center gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-accent text-accent" />
        ))}
      </div>
      <p className={`mb-6 leading-relaxed ${isPlayful ? 'text-text-primary italic' : 'text-text-secondary'}`}>
        "{quote}"
      </p>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
          isPlayful ? 'bg-accent/20' : 'bg-bg-secondary'
        }`}>
          {avatar}
        </div>
        <div>
          <div className="font-medium text-text-primary">{author}</div>
          <div className="text-sm text-text-tertiary">{role}</div>
        </div>
      </div>
    </div>
  );
}

function MiniFeature({
  icon,
  title,
  description,
  delay = 0,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollAnimation(0.2);

  return (
    <div
      ref={ref}
      className={`flex items-start gap-4 p-4 rounded-xl hover:bg-bg-secondary transition-all duration-500
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-text-primary mb-1">{title}</h4>
        <p className="text-sm text-text-secondary">{description}</p>
      </div>
    </div>
  );
}

// Features Grid Section with scroll animations and proper mobile order
function FeaturesGridSection() {
  const { ref, isVisible } = useScrollAnimation(0.1);

  // Features in logical mobile order: Cross-platform → sync → privacy → digests → ratings → streaks
  const features = [
    { icon: <Smartphone />, title: "Cross-platform", description: "Web, iOS, and Android. Your wins sync everywhere.", order: 1 },
    { icon: <Zap />, title: "Real-time sync", description: "Log on any device, see it instantly on others.", order: 2 },
    { icon: <Shield />, title: "Private & secure", description: "Your accomplishments are yours. End-to-end encrypted.", order: 3 },
    { icon: <Calendar />, title: "Weekly digests", description: "Automatic summaries delivered to your inbox.", order: 4 },
    { icon: <Heart />, title: "Satisfaction ratings", description: "Track how your wins make you feel over time.", order: 5 },
    { icon: <Trophy />, title: "Streak tracking", description: "Build momentum with gentle, guilt-free streaks.", order: 6 },
  ];

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-display text-3xl sm:text-4xl text-text-primary mb-4">
            Everything you need, nothing you don't
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <MiniFeature
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default LandingPage;
